
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import gMapLoader from '@/mixins/gMapLoader'
import { firstViewPartners } from '@/utils/partnerLocator'

export default {
  name: 'PartnerLocatorMap',
  mixins: [gMapLoader],
  props: {
    partners: {
      type: Array,
      default: () => [],
    },
    highlightId: {
      type: Number,
      default: null,
    },
    origin: {
      type: Object,
      default: null,
    },
    fitIds: {
      type: Array,
      default: null,
    },
    cluster: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      failed: false,
    }
  },
  watch: {
    partners() {
      this.renderMarkers()
    },
    highlightId(id, previous) {
      this.highlight(previous, false)
      this.highlight(id, true)
    },
    origin() {
      this.renderOrigin()
      this.fit()
    },
    fitIds() {
      this.fit()
    },
  },
  created() {
    this.markers = new Map()
    this.clusterer = null
    this.originMarker = null
    this.lib = null
    this.gmap = null
  },
  mounted() {
    window.gm_authFailure = () => {
      this.failed = true
    }
    this.initMap()
    this.gMapLoader
      .load()
      .then(async ({ maps }) => {
        const { AdvancedMarkerElement, PinElement } = await maps.importLibrary(
          'marker'
        )
        this.lib = { maps, AdvancedMarkerElement, PinElement }
        this.gmap = new maps.Map(this.$refs.map, {
          center: { lat: 46.6, lng: 2.4 },
          zoom: 5,
          mapId: 'WCKAyxjj8NgnMhZ89',
          disableDefaultUI: true,
          zoomControl: this.controls,
          clickableIcons: false,
          gestureHandling: 'greedy',
        })
        this.gmap.addListener('idle', () => {
          const bounds = this.gmap.getBounds()
          if (!bounds) return
          const ne = bounds.getNorthEast()
          const sw = bounds.getSouthWest()
          this.$emit('bounds', {
            north: ne.lat(),
            east: ne.lng(),
            south: sw.lat(),
            west: sw.lng(),
          })
        })
        this.gmap.addListener('click', () => this.$emit('select', null))
        this.renderMarkers()
        this.renderOrigin()
        this.fit()
      })
      .catch(() => {
        this.failed = true
      })
  },
  beforeDestroy() {
    if (this.clusterer) this.clusterer.clearMarkers()
    this.markers.forEach(({ marker }) => (marker.map = null))
  },
  methods: {
    renderMarkers() {
      if (!this.lib || !this.gmap) return
      const { AdvancedMarkerElement, PinElement } = this.lib
      if (this.clusterer) this.clusterer.clearMarkers()
      this.markers.forEach(({ marker }) => (marker.map = null))
      this.markers = new Map()
      const markers = this.partners.map(partner => {
        const color = (partner.types[0] && partner.types[0].color) || '#6b7280'
        const pin = new PinElement({
          background: color,
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 0.9,
        })
        const marker = new AdvancedMarkerElement({
          position: { lat: partner.lat, lng: partner.lng },
          content: pin.element,
          title: partner.company,
          gmpClickable: true,
          map: this.cluster ? null : this.gmap,
        })
        marker.addEventListener('gmp-click', () =>
          this.$emit('select', partner)
        )
        this.markers.set(partner.id, { marker, pin })
        return marker
      })
      if (this.cluster) {
        this.clusterer = new MarkerClusterer({
          map: this.gmap,
          markers,
          renderer: {
            render: ({ count, position }) => {
              const size = Math.min(56, 30 + Math.log2(count) * 5)
              const bubble = document.createElement('div')
              bubble.textContent = count
              bubble.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:#0d1b2a;color:#fff;font:700 13px/1 sans-serif;display:flex;align-items:center;justify-content:center;border:3px solid #ffa41c;box-shadow:0 2px 8px rgba(0,0,0,.3)`
              return new AdvancedMarkerElement({
                position,
                content: bubble,
                zIndex: 500 + count,
              })
            },
          },
        })
      }
      this.highlight(this.highlightId, true)
    },
    renderOrigin() {
      if (!this.lib || !this.gmap) return
      if (this.originMarker) this.originMarker.map = null
      this.originMarker = null
      if (!this.origin) return
      const dot = document.createElement('div')
      dot.style.cssText =
        'width:18px;height:18px;border-radius:50%;background:#1a73e8;border:3px solid #fff;box-shadow:0 0 0 6px rgba(26,115,232,.25)'
      this.originMarker = new this.lib.AdvancedMarkerElement({
        position: { lat: this.origin.lat, lng: this.origin.lng },
        content: dot,
        map: this.gmap,
        zIndex: 1000,
      })
    },
    highlight(id, on) {
      const entry = id ? this.markers.get(id) : null
      if (!entry) return
      entry.pin.scale = on ? 1.5 : 0.9
      entry.marker.zIndex = on ? 999 : null
      if (on && this.cluster && !entry.marker.map) {
        entry.marker.map = this.gmap
      }
    },
    fit() {
      if (!this.lib || !this.gmap) return
      const ids = this.fitIds
      const targets = ids
        ? this.partners.filter(p => ids.includes(p.id))
        : this.origin
        ? []
        : firstViewPartners(this.partners)
      if (!targets.length && !this.origin) return
      const bounds = new this.lib.maps.LatLngBounds()
      targets.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }))
      if (this.origin) bounds.extend(this.origin)
      if (targets.length === 0 && this.origin) {
        this.gmap.setCenter(this.origin)
        this.gmap.setZoom(11)
        return
      }
      this.gmap.fitBounds(bounds, 48)
      if (targets.length === 1 && !this.origin) this.gmap.setZoom(13)
    },
    focus(partner, zoom = 13) {
      if (!this.gmap || !partner) return
      this.gmap.panTo({ lat: partner.lat, lng: partner.lng })
      if (this.gmap.getZoom() < zoom) this.gmap.setZoom(zoom)
    },
  },
}
